import { useCallback, useRef } from 'react';

const normalizeContent = (value) => JSON.stringify(value ?? '');

export function useGenerationTracking() {
  const regenerationCount = useRef(0);
  const generationStartTime = useRef(null);

  const onGenerationStart = useCallback(() => {
    regenerationCount.current += 1;
    generationStartTime.current = Date.now();
  }, []);

  const buildEditDescription = useCallback((original, final) => {
    if (Array.isArray(original) && Array.isArray(final)) {
      const changedFields = new Set();
      final.forEach((item, index) => {
        const before = original[index] || {};
        if (normalizeContent(before.proposedCorrect ?? before.correct_answer) !== normalizeContent(item.proposedCorrect ?? item.correct_answer)) {
          changedFields.add('answer corrected');
        }
        if (normalizeContent(before.proposedDetail ?? before.detail_answer) !== normalizeContent(item.proposedDetail ?? item.detail_answer)) {
          changedFields.add('working steps changed');
        }
        if (normalizeContent(before.question_text) !== normalizeContent(item.question_text)) {
          changedFields.add('question text changed');
        }
      });
      if (changedFields.size) {
        return Array.from(changedFields).join('; ');
      }
    }

    if (original && final && typeof original === 'object' && typeof final === 'object') {
      const changedFields = [];
      if (normalizeContent(original.proposedCorrect ?? original.correct_answer) !== normalizeContent(final.proposedCorrect ?? final.correct_answer)) {
        changedFields.push('answer corrected');
      }
      if (normalizeContent(original.proposedDetail ?? original.detail_answer) !== normalizeContent(final.proposedDetail ?? final.detail_answer)) {
        changedFields.push('working steps changed');
      }
      if (changedFields.length) {
        return changedFields.join('; ');
      }
    }

    const originalText = typeof original === 'string' ? original : normalizeContent(original);
    const finalText = typeof final === 'string' ? final : normalizeContent(final);
    if (!originalText || !finalText) return '';
    const diff = Math.abs(originalText.length - finalText.length);
    if (diff < 10) return 'Minor edits';
    if (finalText.length < originalText.length) return 'Content shortened or removed';
    return 'Content expanded or corrected';
  }, []);

  const buildImplicitSignals = useCallback((outcome, originalContent, finalContent) => {
    const edited = normalizeContent(originalContent) !== normalizeContent(finalContent);
    return {
      outcome: edited ? 'edited' : outcome,
      regeneration_count: Math.max(0, regenerationCount.current - 1),
      teacher_edited: edited,
      edit_description: edited ? buildEditDescription(originalContent, finalContent) : '',
    };
  }, [buildEditDescription]);

  const resetTracking = useCallback(() => {
    regenerationCount.current = 0;
    generationStartTime.current = null;
  }, []);

  return { onGenerationStart, buildImplicitSignals, resetTracking };
}
